import React, { useEffect, useState } from 'react';
import { ShoppingCart, X, MessageSquare, Package, AlertCircle, Tag, CheckCircle2 } from 'lucide-react';
import { fetchOrders, fetchOrder, updateOrderStatus, updateOrderNotes, submitQuote } from '../../api/supplierApi';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_LABEL = {
  pending:              { tr: 'Bekliyor',           en: 'Pending',              color: 'bg-orange-100 text-orange-700' },
  quote_pending:        { tr: 'Teklif Bekleniyor',  en: 'Quote Pending',        color: 'bg-purple-100 text-purple-700' },
  quoted:               { tr: 'Teklif Verildi',     en: 'Quoted',               color: 'bg-blue-100 text-blue-700' },
  pending_confirmation: { tr: 'Onay Bekliyor',      en: 'Pending Confirmation', color: 'bg-yellow-100 text-yellow-700' },
  acknowledged:         { tr: 'Alındı',             en: 'Acknowledged',         color: 'bg-sky-100 text-sky-700' },
  confirmed:            { tr: 'Onaylandı',          en: 'Confirmed',            color: 'bg-teal-100 text-teal-700' },
  in_progress:          { tr: 'Devam Ediyor',       en: 'In Progress',          color: 'bg-indigo-100 text-indigo-700' },
  completed:            { tr: 'Tamamlandı',         en: 'Completed',            color: 'bg-emerald-100 text-emerald-700' },
  cancelled:            { tr: 'İptal Edildi',       en: 'Cancelled',            color: 'bg-red-100 text-red-700' },
};

// Status machine for PUT /status endpoint (legacy + direct orders only)
const TRANSITIONS = {
  pending:              'acknowledged',
  acknowledged:         'in_progress',
  pending_confirmation: 'confirmed',
  confirmed:            'in_progress',
  in_progress:          'completed',
};

const TRANSITION_LABEL = {
  acknowledged: { tr: 'Siparişi Al',  en: 'Acknowledge' },
  in_progress:  { tr: 'Başla',        en: 'Start' },
  completed:    { tr: 'Tamamla',      en: 'Complete' },
  confirmed:    { tr: 'Onayla',       en: 'Confirm' },
};

// ── Quote Submit Modal ────────────────────────────────────────────────────────
function QuoteSubmitModal({ order, tr, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    offered_price: '',
    currency: 'USD',
    lead_time_days: '',
    validity_date: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  const isValid =
    form.offered_price && parseFloat(form.offered_price) > 0 &&
    form.lead_time_days !== '' && parseInt(form.lead_time_days, 10) >= 0 &&
    form.validity_date;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    try {
      const updated = await submitQuote(order.id, {
        offered_price:  parseFloat(form.offered_price),
        currency:       form.currency,
        lead_time_days: parseInt(form.lead_time_days, 10),
        validity_date:  form.validity_date,
        notes:          form.notes,
      });
      onSubmitted(updated);
      toast.success(tr ? 'Teklif başarıyla gönderildi.' : 'Quote submitted successfully.');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Teklif gönderilemedi.' : 'Failed to submit quote.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-4 border-b flex items-start justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{tr ? 'Teklif Ver' : 'Submit Quote'}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{order.order_title || order.order_number}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Price + Currency */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1.5 block">
                {tr ? 'Teklif Fiyatı' : 'Offered Price'} <span className="text-red-400">*</span>
              </label>
              <input
                type="number" min="0.01" step="0.01"
                value={form.offered_price}
                onChange={e => setForm(p => ({ ...p, offered_price: e.target.value }))}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                placeholder="0.00"
                data-testid="quote-offered-price"
              />
            </div>
            <div className="w-28">
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1.5 block">
                {tr ? 'Döviz' : 'Currency'}
              </label>
              <select
                value={form.currency}
                onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="quote-currency"
              >
                {['USD', 'EUR', 'TRY', 'GBP', 'AED'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Lead Time */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1.5 block">
              {tr ? 'Teslim Süresi (gün)' : 'Lead Time (days)'} <span className="text-red-400">*</span>
            </label>
            <input
              type="number" min="0"
              value={form.lead_time_days}
              onChange={e => setForm(p => ({ ...p, lead_time_days: e.target.value }))}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              placeholder="14"
              data-testid="quote-lead-time"
            />
          </div>

          {/* Validity Date */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1.5 block">
              {tr ? 'Geçerlilik Tarihi' : 'Validity Date'} <span className="text-red-400">*</span>
            </label>
            <input
              type="date" min={minDateStr}
              value={form.validity_date}
              onChange={e => setForm(p => ({ ...p, validity_date: e.target.value }))}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              data-testid="quote-validity-date"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1.5 block">
              {tr ? 'Notlar' : 'Notes'}
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
              placeholder={tr ? 'Ödeme koşulları, teslimat detayı vb...' : 'Payment terms, delivery details, etc...'}
              data-testid="quote-notes"
            />
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3">
          <Button
            onClick={handleSubmit}
            disabled={submitting || !isValid}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
            data-testid="quote-submit-btn"
          >
            {submitting
              ? (tr ? 'Gönderiliyor...' : 'Submitting...')
              : (tr ? 'Teklifi Gönder' : 'Send Quote')}
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-xl">
            {tr ? 'Vazgeç' : 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Order Detail Modal ────────────────────────────────────────────────────────
function OrderDetailModal({ orderId, tr, onClose, onUpdated }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  useEffect(() => {
    fetchOrder(orderId)
      .then(o => { setOrder(o); setNotes(o.supplier_notes || ''); })
      .catch(() => toast.error(tr ? 'Sipariş yüklenemedi.' : 'Failed to load order.'))
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleAdvance = async () => {
    const next = TRANSITIONS[order.status];
    if (!next) return;
    setAdvancing(true);
    try {
      const updated = await updateOrderStatus(order.id, next, notes);
      setOrder(updated);
      onUpdated(updated);
      toast.success(tr ? 'Sipariş durumu güncellendi.' : 'Order status updated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Hata.' : 'Error.'));
    } finally { setAdvancing(false); }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const updated = await updateOrderNotes(order.id, notes);
      setOrder(updated);
      toast.success(tr ? 'Not kaydedildi.' : 'Notes saved.');
    } catch (e) {
      toast.error(tr ? 'Not kaydedilemedi.' : 'Failed to save notes.');
    } finally { setSavingNotes(false); }
  };

  // Normalize items: support both legacy ordered_items and customer items
  const getDisplayItems = (o) => {
    if (o.ordered_items?.length) return o.ordered_items;
    if (o.items?.length) return o.items.map(i => ({
      title:      i.product_name || i.title || 'Ürün',
      quantity:   i.quantity,
      unit:       i.variant_name || 'adet',
      unit_price: i.unit_price,
      currency:   i.currency || 'USD',
      note:       i.variant_name || '',
    }));
    return [];
  };

  const isExpiredQuote = (o) => {
    const vo = o?.quote_offer?.validity_date;
    if (!vo) return false;
    try { return new Date(vo) < new Date(); } catch { return false; }
  };

  if (showQuoteForm && order) {
    return (
      <QuoteSubmitModal
        order={order}
        tr={tr}
        onClose={() => setShowQuoteForm(false)}
        onSubmitted={(updated) => {
          setOrder(updated);
          setShowQuoteForm(false);
          onUpdated(updated);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin h-10 w-10 border-4 border-amber-500 border-t-transparent rounded-full" />
          </div>
        ) : !order ? (
          <div className="p-6 text-center text-red-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            {tr ? 'Sipariş bulunamadı.' : 'Order not found.'}
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_LABEL[order.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                    {tr ? STATUS_LABEL[order.status]?.tr : STATUS_LABEL[order.status]?.en}
                  </span>
                  {order.order_type === 'quote' && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                      <Tag className="h-3 w-3" />{tr ? 'Teklif Talebi' : 'Quote Request'}
                    </span>
                  )}
                  {order.order_type === 'direct' && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">
                      {tr ? 'Direkt Sipariş' : 'Direct Order'}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">{order.order_number}</span>
                </div>
                <h3 className="font-bold text-gray-900">{order.order_title || order.order_number}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{new Date(order.created_at).toLocaleString()}</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-4">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Summary / Brief */}
              {(order.summary || order.brief) && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-1">
                    {tr ? 'Özet / Brifing' : 'Summary / Brief'}
                  </p>
                  <p className="text-sm text-gray-700">{order.summary || order.brief}</p>
                </div>
              )}

              {/* Items */}
              {getDisplayItems(order).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2 flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5" />{tr ? 'Sipariş Kalemleri' : 'Ordered Items'}
                  </p>
                  <div className="space-y-2">
                    {getDisplayItems(order).map((item, i) => (
                      <div key={i} className="flex items-center justify-between bg-amber-50 rounded-xl px-3 py-2.5">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{item.title}</p>
                          {item.note && <p className="text-xs text-gray-500">{item.note}</p>}
                        </div>
                        <div className="text-right">
                          {item.quantity && <p className="text-sm text-gray-700">{item.quantity} {item.unit || ''}</p>}
                          {item.unit_price && (
                            <p className="text-xs font-bold text-amber-600">
                              {item.currency || ''} {parseFloat(item.unit_price).toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {order.total_amount > 0 && (
                    <p className="text-right text-sm font-bold text-gray-700 mt-2">
                      {tr ? 'Toplam:' : 'Total:'} {order.currency || 'USD'} {parseFloat(order.total_amount).toFixed(2)}
                    </p>
                  )}
                </div>
              )}

              {/* Service Items (legacy) */}
              {order.service_items?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
                    {tr ? 'Hizmet Kalemleri' : 'Service Items'}
                  </p>
                  <div className="space-y-2">
                    {order.service_items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between bg-sky-50 rounded-xl px-3 py-2.5">
                        <p className="text-sm font-medium text-gray-800">{item.title}</p>
                        {item.unit_price && (
                          <p className="text-xs font-bold text-sky-600">
                            {item.currency || ''} {item.unit_price.toFixed(2)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quote Offer — shown when status is "quoted" */}
              {order.status === 'quoted' && order.quote_offer && (
                <div className={`rounded-xl p-4 border ${isExpiredQuote(order) ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                  <p className="text-xs font-semibold uppercase mb-3 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {isExpiredQuote(order)
                      ? (tr ? 'Teklif Süresi Doldu' : 'Quote Expired')
                      : (tr ? 'Gönderilen Teklif' : 'Submitted Quote')}
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">{tr ? 'Teklif Fiyatı' : 'Offered Price'}</p>
                      <p className="font-bold text-gray-900">
                        {order.quote_offer.currency} {parseFloat(order.quote_offer.offered_price).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{tr ? 'Teslim Süresi' : 'Lead Time'}</p>
                      <p className="font-bold text-gray-900">
                        {order.quote_offer.lead_time_days} {tr ? 'gün' : 'days'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{tr ? 'Geçerlilik' : 'Valid Until'}</p>
                      <p className={`font-medium ${isExpiredQuote(order) ? 'text-red-600' : 'text-gray-700'}`}>
                        {new Date(order.quote_offer.validity_date).toLocaleDateString()}
                        {isExpiredQuote(order) && ` (${tr ? 'Süresi doldu' : 'Expired'})`}
                      </p>
                    </div>
                  </div>
                  {order.quote_offer.notes && (
                    <p className="text-xs text-gray-600 mt-3 bg-white/70 rounded-lg px-3 py-2">
                      {order.quote_offer.notes}
                    </p>
                  )}
                </div>
              )}

              {order.delivery_deadline && (
                <div className="bg-orange-50 rounded-xl px-4 py-3">
                  <p className="text-xs font-semibold text-orange-600">{tr ? 'Teslimat Tarihi' : 'Delivery Deadline'}</p>
                  <p className="text-sm font-medium text-gray-800 mt-0.5">
                    {new Date(order.delivery_deadline).toLocaleDateString()}
                  </p>
                </div>
              )}

              {order.notes_from_admin && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-blue-600 mb-1">{tr ? 'Admin Notu' : 'Admin Notes'}</p>
                  <p className="text-sm text-gray-700">{order.notes_from_admin}</p>
                </div>
              )}

              {/* Supplier Notes */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2 flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" />{tr ? 'Notunuz' : 'Your Notes'}
                </p>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                  placeholder={tr ? 'Siparişe not ekleyin...' : 'Add a note to this order...'}
                />
                <Button
                  size="sm" onClick={handleSaveNotes} disabled={savingNotes}
                  className="mt-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                >
                  {savingNotes ? (tr ? 'Kaydediliyor...' : 'Saving...') : (tr ? 'Notu Kaydet' : 'Save Note')}
                </Button>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 pb-6 flex gap-3 flex-wrap">
              {/* Teklif Ver — only for quote_pending orders */}
              {order.status === 'quote_pending' && (
                <Button
                  onClick={() => setShowQuoteForm(true)}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
                  data-testid="open-quote-form-btn"
                >
                  {tr ? 'Teklif Ver' : 'Submit Quote'}
                </Button>
              )}

              {/* Advance status — for non-quote statuses */}
              {TRANSITIONS[order.status] && order.status !== 'quote_pending' && (
                <Button
                  onClick={handleAdvance}
                  disabled={advancing}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
                  data-testid="advance-order-btn"
                >
                  {advancing ? '...' : (
                    tr
                      ? TRANSITION_LABEL[TRANSITIONS[order.status]]?.tr
                      : TRANSITION_LABEL[TRANSITIONS[order.status]]?.en
                  )}
                </Button>
              )}

              <Button variant="outline" onClick={onClose} className="rounded-xl flex-1">
                {tr ? 'Kapat' : 'Close'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
export default function OrdersSection({ tr }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [quoteOrderId, setQuoteOrderId] = useState(null);

  const load = (status = '') => {
    setLoading(true);
    fetchOrders(status ? { status } : {})
      .then(setOrders)
      .catch(() => toast.error(tr ? 'Siparişler yüklenemedi.' : 'Failed to load orders.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(statusFilter); }, [statusFilter]);

  const handleUpdated = (updated) =>
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));

  const STATUS_TABS = [
    { key: '',                    label: tr ? 'Tümü'             : 'All' },
    { key: 'quote_pending',       label: tr ? 'Teklif Bekleniyor': 'Quote Pending' },
    { key: 'quoted',              label: tr ? 'Teklif Verildi'   : 'Quoted' },
    { key: 'pending_confirmation',label: tr ? 'Onay Bekliyor'    : 'Pending' },
    { key: 'in_progress',         label: tr ? 'Devam Ediyor'     : 'In Progress' },
    { key: 'completed',           label: tr ? 'Tamamlandı'       : 'Completed' },
  ];

  const quoteOrderForModal = orders.find(o => o.id === quoteOrderId);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{tr ? 'Siparişlerim' : 'My Orders'}</h2>
        <p className="text-sm text-gray-500 mt-0.5">{orders.length} {tr ? 'sipariş' : 'orders'}</p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit overflow-x-auto">
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setStatusFilter(t.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              statusFilter === t.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`filter-tab-${t.key || 'all'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center h-48 items-center">
          <div className="animate-spin h-10 w-10 border-4 border-amber-500 border-t-transparent rounded-full" />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center h-48 justify-center text-center bg-white rounded-2xl border border-gray-100">
          <ShoppingCart className="h-12 w-12 text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">
            {tr ? 'Bu kategoride sipariş yok.' : 'No orders in this category.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">
                  {tr ? 'Sipariş' : 'Order'}
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 hidden md:table-cell">
                  {tr ? 'Tür' : 'Type'}
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 hidden md:table-cell">
                  {tr ? 'Tarih' : 'Date'}
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">
                  {tr ? 'Durum' : 'Status'}
                </th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {orders.map(o => {
                const sl = STATUS_LABEL[o.status] || {
                  color: 'bg-gray-100 text-gray-600', tr: o.status, en: o.status,
                };
                const isNew = ['quote_pending', 'pending', 'acknowledged'].includes(o.status)
                  && o.created_at && (Date.now() - new Date(o.created_at).getTime()) < 24 * 60 * 60 * 1000;
                return (
                  <tr key={o.id}
                      className={`transition-colors ${isNew ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'}`}
                      data-testid={isNew ? `order-row-new-${o.id}` : undefined}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{o.order_title || o.order_number}</p>
                        {isNew && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white animate-pulse shadow-sm">
                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                            {tr ? 'YENİ' : 'NEW'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{o.order_number}</p>
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      {o.order_type === 'quote' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 inline-flex items-center gap-1">
                          <Tag className="h-3 w-3" />{tr ? 'Teklif' : 'Quote'}
                        </span>
                      ) : o.order_type === 'direct' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">
                          {tr ? 'Direkt' : 'Direct'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <p className="text-sm text-gray-600">
                        {new Date(o.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${sl.color}`}>
                        {tr ? sl.tr : sl.en}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        {/* Quick "Teklif Ver" directly from the list row */}
                        {o.status === 'quote_pending' && (
                          <Button
                            size="sm"
                            onClick={() => setQuoteOrderId(o.id)}
                            className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs"
                            data-testid={`teklif-ver-btn-${o.id}`}
                          >
                            {tr ? 'Teklif Ver' : 'Quote'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDetailId(o.id)}
                          className="text-amber-600 hover:bg-amber-50 rounded-lg text-xs"
                          data-testid={`order-detail-btn-${o.id}`}
                        >
                          {tr ? 'Detay' : 'Detail'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {detailId && (
        <OrderDetailModal
          orderId={detailId}
          tr={tr}
          onClose={() => setDetailId(null)}
          onUpdated={handleUpdated}
        />
      )}

      {/* Quick Quote Submit Modal from list row */}
      {quoteOrderId && quoteOrderForModal && (
        <QuoteSubmitModal
          order={quoteOrderForModal}
          tr={tr}
          onClose={() => setQuoteOrderId(null)}
          onSubmitted={(updated) => {
            handleUpdated(updated);
            setQuoteOrderId(null);
          }}
        />
      )}
    </div>
  );
}
